import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import Login from "@/pages/Login";
import Today from "@/pages/Today";
import TripleView from "@/pages/TripleView";
import Player from "@/pages/Player";
import Accuracy from "@/pages/Accuracy";
import Upload from "@/pages/Upload";
import Settings from "@/pages/Settings";
import OddsManager from "@/pages/OddsManager";
import SeasonModel from "@/pages/SeasonModel";
import Account from "@/pages/Account";
import Teams from "@/pages/Teams";
import Standings from "@/pages/Standings";
import StatLeaders from "@/pages/StatLeaders";
import StatsTable from "@/pages/StatsTable";
import Schedule from "@/pages/Schedule";
import Europe from "@/pages/Europe";
import EuroTeam from "@/pages/EuroTeam";
import EuroGame from "@/pages/EuroGame";
import Rotation from "@/pages/Rotation";
import Rotations from "@/pages/Rotations";
import HalfTimeValue from "@/pages/HalfTimeValue";
import Analysis from "@/pages/Analysis";
import PrintValue from "@/pages/PrintValue";
import AuthCallback from "@/pages/AuthCallback";
import ResetPassword from "@/pages/ResetPassword";
import Privacy from "@/pages/Privacy";

function Protected({ children, admin = false, bare = false }) {
  const { user } = useAuth();
  if (user === null) {
    return <div data-testid="auth-loading" className="min-h-screen grid place-items-center text-slate-400 font-display uppercase tracking-[0.3em]">Loading</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return bare ? children : <AppShell>{children}</AppShell>;
}

function AppRoutes() {
  const location = useLocation();
  // Google sign-in returns with #session_id=...; handle it before any protected route runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const fresh = location.state?.fresh || "page";
  return (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Protected><TripleView key={fresh} /></Protected>} />
          <Route path="/team-projections" element={<Protected><TripleView key={`team-${fresh}`} center="team" /></Protected>} />
          <Route path="/today" element={<Protected><Today key={fresh} /></Protected>} />
          <Route path="/player/:id" element={<Protected><Player /></Protected>} />
          <Route path="/accuracy" element={<Protected admin><Accuracy /></Protected>} />
          <Route path="/upload" element={<Protected admin><Upload /></Protected>} />
          <Route path="/rotation" element={<Protected admin><Rotation /></Protected>} />
          <Route path="/rotations" element={<Protected admin><Rotations /></Protected>} />
          <Route path="/halftime-value" element={<Protected admin><HalfTimeValue /></Protected>} />
          <Route path="/value/print" element={<Protected admin bare><PrintValue /></Protected>} />
          <Route path="/settings" element={<Protected admin><Settings /></Protected>} />
          <Route path="/odds-manager" element={<Protected admin><OddsManager /></Protected>} />
          <Route path="/season-model" element={<Protected admin><SeasonModel /></Protected>} />
          <Route path="/teams" element={<Protected><Teams /></Protected>} />
          <Route path="/teams/:id" element={<Protected><Teams /></Protected>} />
          <Route path="/standings" element={<Protected><Standings key={fresh} /></Protected>} />
          <Route path="/schedule" element={<Protected><Schedule /></Protected>} />
          <Route path="/stats" element={<Protected><StatLeaders /></Protected>} />
          <Route path="/stats/all" element={<Protected><StatsTable /></Protected>} />
          <Route path="/analysis" element={<Protected admin><Analysis /></Protected>} />
          <Route path="/europe" element={<Protected><Europe /></Protected>} />
          <Route path="/europe/:comp" element={<Protected><Europe /></Protected>} />
          <Route path="/europe/:comp/teams/:code" element={<Protected><EuroTeam /></Protected>} />
          <Route path="/europe/:comp/games/:gameId" element={<Protected><EuroGame /></Protected>} />
          <Route path="/account" element={<Protected><Account /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" />
    </AuthProvider>
  );
}

export default App;
